// ─── Attribute values: parse, convert, format ───
// MIRROR of pim/app/gnp/services/attributeValue.ts. Edit the pim copy and copy
// it across — the two must stay identical or the site and PIM will disagree
// about what a stored value means.
//
// Plan: docs/plans/PLAN_PIM_ATTRIBUTE_TYPED_VALUES.md
//
// Parsing has THREE outcomes, not two. A value that parses cleanly but carries
// a qualifier ("201* Lbs" — Gary's mark for an estimated weight that still
// needs physically weighing) is neither a plain conversion nor a failure.
// Collapsing that into either one loses the flag on 106 items.

export type UnitOfMeasure = {
  Id: number;
  Name: string;
  Symbol: string;
  Dimension: string | null;
  ToBase: number | null;
  ToBaseOffset: number | null;
  DisplayStyle: string | null;
};

export type AttributeDef = {
  Id: number;
  Name: string;
  ValueType: number;
  Dimension?: string | null;
  BaseUnitOfMeasureId?: number | null;
  DisplayUnitOfMeasureId?: number | null;
  Decimals?: number | null;
  SupportsEstimated?: boolean;
  /**
   * Redundant labels the warehouse types that restate the attribute name and
   * carry no unit — "330 KH" on Key Hours, "11857 HD" on Deadman Hours.
   * Declared per attribute on purpose: "KH" is only meaningless on Key Hours.
   */
  LabelSuffixes?: string[];
  Prefix?: string | null;
  Suffix?: string | null;
};

export type ParsedValue = {
  value: number;
  unitOfMeasureId: number | null;
  unitOfMeasureSymbol: string | null;
  baseValue: number;
  prefix: string;
  suffix: string;
};

export type ParseResult =
  | ({ kind: "value" } & ParsedValue)
  | ({ kind: "qualified"; isEstimated: true } & ParsedValue)
  | { kind: "empty" }
  | { kind: "quarantine"; reason: string };

/** Values that mean "nobody filled this in" — never 0, never a conversion. */
const SENTINELS = new Set([
  "n/a", "na", "n\\a", "tbd", "tdb", "-", "--", "?", "none", "null", "unknown",
]);

const FRACTION = String.raw`\d+\s*\/\s*\d+`;
// 1,200 | .5 | 30.5 | 96 | 3 1/4 | 3-21/23 | 1/2
const QTY = String.raw`(?:\d{1,3}(?:,\d{3})+|\d*\.?\d+)(?:[\s-]+${FRACTION})?|${FRACTION}`;

const INCH_MARK = String.raw`(?:"|”|''|\s*in\b\.?|\s*inch(?:es)?\b)`;
const FOOT_MARK = String.raw`(?:'|’|\s*ft\b\.?|\s*feet\b|\s*foot\b)`;

/** Unit words as authored, mapped to the canonical UnitOfMeasure.Name. */
const UNIT_WORDS: Array<[RegExp, string]> = [
  [/^(?:lbs?|lb|pounds?)\.?$/i, "Pounds"],
  [/^(?:kgs?|kilograms?)\.?$/i, "Kilograms"],
  [/^(?:mm|millimet(?:er|re)s?)\.?$/i, "Millimetres"],
  [/^(?:in|inch|inches)\.?$/i, "Inches"],
  [/^(?:ft|feet|foot)\.?$/i, "Feet"],
  [/^(?:gal|gallons?)\.?$/i, "Gallons"],
  [/^(?:ah|amp\s*hours?)\.?$/i, "Amp Hours"],
];

const stripNoise = (raw: string) =>
  raw.replace(/\u00a0/g, " ").replace(/\s+/g, " ").trim();

/** "3 1/4" -> 3.25, "3-21/23" -> 3.913…, "1/2" -> 0.5, "1,200" -> 1200 */
const toNumber = (text: string): number | null => {
  const s = text.replace(/,/g, "").trim();
  if (!s) return null;

  const mixed = s.match(/^(\d*\.?\d+)[\s-]+(\d+)\s*\/\s*(\d+)$/);
  if (mixed) {
    const denominator = Number(mixed[3]);
    if (!denominator) return null;
    return Number(mixed[1]) + Number(mixed[2]) / denominator;
  }

  const fraction = s.match(/^(\d+)\s*\/\s*(\d+)$/);
  if (fraction) {
    const denominator = Number(fraction[2]);
    if (!denominator) return null;
    return Number(fraction[1]) / denominator;
  }

  if (!/^\d*\.?\d+$/.test(s)) return null;
  const n = Number(s);
  return Number.isFinite(n) ? n : null;
};

const unitByName = (units: UnitOfMeasure[], name: string) =>
  units.find(u => u.Name.toLowerCase() === name.toLowerCase()) ?? null;

const unitById = (units: UnitOfMeasure[], id: number | null | undefined) =>
  id == null ? null : units.find(u => u.Id === id) ?? null;

/** value in `unit` -> the dimension's base unit. */
export const toBaseValue = (value: number, unit: UnitOfMeasure | null): number => {
  if (!unit || unit.ToBase == null) return value;
  return value * unit.ToBase + (unit.ToBaseOffset ?? 0);
};

/** base-unit value -> `unit`. Inverse of toBaseValue. */
export const fromBaseValue = (baseValue: number, unit: UnitOfMeasure | null): number => {
  if (!unit || unit.ToBase == null || unit.ToBase === 0) return baseValue;
  return (baseValue - (unit.ToBaseOffset ?? 0)) / unit.ToBase;
};

/** Convert between any two units of the same dimension, via the base. */
export const convert = (value: number, from: UnitOfMeasure | null, to: UnitOfMeasure | null) =>
  fromBaseValue(toBaseValue(value, from), to);

const build = (
  value: number,
  unit: UnitOfMeasure | null,
  def: AttributeDef,
  isEstimated: boolean,
): ParseResult => {
  const parsed: ParsedValue = {
    value,
    unitOfMeasureId: unit?.Id ?? null,
    unitOfMeasureSymbol: unit?.Symbol ?? null,
    baseValue: toBaseValue(value, unit),
    prefix: def.Prefix ?? "",
    suffix: unit?.Symbol ?? def.Suffix ?? "",
  };
  return isEstimated ? { kind: "qualified", isEstimated: true, ...parsed } : { kind: "value", ...parsed };
};

/**
 * Parse an authored attribute string. Returns `quarantine` rather than guessing
 * — a wrong unit silently corrupts a measurement, and there is no way to tell
 * afterwards which values were invented.
 */
export function parseAttributeValue(
  raw: unknown,
  def: AttributeDef,
  units: UnitOfMeasure[],
): ParseResult {
  if (raw === null || raw === undefined) return { kind: "empty" };
  if (typeof raw === "number") {
    if (!Number.isFinite(raw)) return { kind: "quarantine", reason: "non-finite number" };
    return build(raw, unitById(units, def.DisplayUnitOfMeasureId), def, false);
  }
  if (typeof raw !== "string") return { kind: "quarantine", reason: `unsupported type ${typeof raw}` };

  let text = stripNoise(raw);
  if (!text) return { kind: "empty" };
  if (SENTINELS.has(text.toLowerCase())) return { kind: "empty" };

  // Gary's estimate mark, taken off before anything else so every later branch
  // sees a clean value. Only a trailing "*" on the number counts — "**DEAD
  // MACHINE**" is prose and must still quarantine.
  let isEstimated = false;
  const estimated = text.match(new RegExp(String.raw`^(${QTY})\s*\*\s*(.*)$`));
  if (estimated) {
    isEstimated = true;
    text = `${estimated[1]} ${estimated[2]}`.trim();
  }

  // Compound feet+inches: 16' 2"  |  18' 6  |  5' 6 1/2"
  const compound = text.match(
    new RegExp(String.raw`^(${QTY})\s*${FOOT_MARK}\s*(${QTY})\s*${INCH_MARK}?$`, "i"),
  );
  if (compound) {
    const feet = toNumber(compound[1]);
    const inches = toNumber(compound[2]);
    if (feet === null || inches === null) return { kind: "quarantine", reason: `unreadable compound "${raw}"` };
    const foot = unitByName(units, "Feet");
    if (!foot) return { kind: "quarantine", reason: "Feet unit not configured" };
    return build(feet + inches / 12, foot, def, isEstimated);
  }

  // Quantity + inch mark: 96"  |  3 1/4"  |  .5"
  const inches = text.match(new RegExp(String.raw`^(${QTY})\s*${INCH_MARK}$`, "i"));
  if (inches) {
    const n = toNumber(inches[1]);
    if (n === null) return { kind: "quarantine", reason: `unreadable quantity "${raw}"` };
    return build(n, unitByName(units, "Inches"), def, isEstimated);
  }

  // Quantity + foot mark: 28'  |  10 ft
  const feetOnly = text.match(new RegExp(String.raw`^(${QTY})\s*${FOOT_MARK}$`, "i"));
  if (feetOnly) {
    const n = toNumber(feetOnly[1]);
    if (n === null) return { kind: "quarantine", reason: `unreadable quantity "${raw}"` };
    return build(n, unitByName(units, "Feet"), def, isEstimated);
  }

  // Quantity + unit word: 1318 Lbs  |  3,400 lbs.  |  120 mm  |  24 kg
  const worded = text.match(new RegExp(String.raw`^(${QTY})\s*([A-Za-z.\s]+)$`));
  if (worded) {
    const n = toNumber(worded[1]);
    const word = worded[2].trim();
    if (n === null) return { kind: "quarantine", reason: `unreadable quantity "${raw}"` };

    const isLabel = (def.LabelSuffixes ?? []).some(
      label => label.toLowerCase() === word.toLowerCase().replace(/\.$/, ""),
    );
    if (isLabel) return build(n, unitById(units, def.DisplayUnitOfMeasureId), def, isEstimated);

    const match = UNIT_WORDS.find(([pattern]) => pattern.test(word));
    if (!match) return { kind: "quarantine", reason: `unrecognised qualifier "${word}"` };
    const unit = unitByName(units, match[1]);
    if (!unit) return { kind: "quarantine", reason: `unit "${match[1]}" not configured` };
    if (def.Dimension && unit.Dimension && def.Dimension !== unit.Dimension) {
      return { kind: "quarantine", reason: `${unit.Name} is not a ${def.Dimension}` };
    }
    return build(n, unit, def, isEstimated);
  }

  // Bare quantity: takes the attribute's declared display unit.
  const bare = text.match(new RegExp(String.raw`^(${QTY})$`));
  if (bare) {
    const n = toNumber(bare[1]);
    if (n === null) return { kind: "quarantine", reason: `unreadable quantity "${raw}"` };
    return build(n, unitById(units, def.DisplayUnitOfMeasureId), def, isEstimated);
  }

  return { kind: "quarantine", reason: `no numeric reading in "${raw}"` };
}

const trimZeros = (s: string) => (s.includes(".") ? s.replace(/\.?0+$/, "") : s);

// Decimals is a ceiling, not a width: a 21in upright reads 21", not 21.00",
// while 26.25 keeps its quarter.
const formatNumber = (n: number, decimals: number | null | undefined) =>
  n.toLocaleString(undefined, {
    minimumFractionDigits: 0,
    maximumFractionDigits: decimals ?? 4,
  });

export type ItemAttribute = {
  Name?: string;
  Value?: unknown;
  UnitOfMeasureId?: number | null;
  UnitOfMeasureSymbol?: string | null;
  BaseValue?: number | null;
  IsEstimated?: boolean;
  Prefix?: string | null;
  Suffix?: string | null;
};

/**
 * Render a stored attribute. Tolerates the pre-migration string shape so it can
 * ship before the backfill — during the migration both shapes exist on the same
 * device, by design (IndexedDB + change feed).
 */
export function formatAttributeValue(
  attr: ItemAttribute | null | undefined,
  def?: AttributeDef | null,
  units: UnitOfMeasure[] = [],
): string {
  if (!attr || attr.Value === null || attr.Value === undefined) return "";
  if (typeof attr.Value === "string") return attr.Value;        // not yet converted
  if (typeof attr.Value === "boolean") return attr.Value ? "Yes" : "No";
  if (typeof attr.Value !== "number") return String(attr.Value);

  const unit = unitById(units, attr.UnitOfMeasureId);
  const prefix = attr.Prefix ?? def?.Prefix ?? "";
  const mark = attr.IsEstimated ? "*" : "";

  // Classic feet-inches, rendered from BaseValue so it is exact integer maths:
  // 194 -> 16' 2",  336 -> 28' (zero remainder suppresses the inches part).
  if (unit?.DisplayStyle === "feetInches" && typeof attr.BaseValue === "number") {
    const totalInches = attr.BaseValue;
    const sign = totalInches < 0 ? "-" : "";
    const magnitude = Math.abs(totalInches);
    const feet = Math.floor(magnitude / 12);
    const inches = magnitude - feet * 12;
    const rounded = Number(inches.toFixed(def?.Decimals ?? 2));
    const feetPart = `${sign}${feet}'`;
    return rounded === 0 ? `${prefix}${feetPart}${mark}` : `${prefix}${feetPart} ${trimZeros(String(rounded))}"${mark}`;
  }

  const suffix = attr.UnitOfMeasureSymbol ?? unit?.Symbol ?? attr.Suffix ?? def?.Suffix ?? "";
  const number = formatNumber(attr.Value, def?.Decimals);
  const spacer = suffix && /^[A-Za-z]/.test(suffix) ? " " : "";
  return `${prefix}${number}${mark}${spacer}${suffix}`;
}
